import { CreativeListRecord, CreativeOfCampaign, RichMediaTemplateId, parseCreativeValue } from 'core/creative/Creative';
import { get } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import videoPlaceholder from 'assets/video-placeholder.svg';
import styles from './creativePreview.module.scss';

const SPRITE_FRAMES = 5; // videoPreview 是 5 張截圖水平拼成的一張大圖
const FRAME_INTERVAL_MS = 1000; // hover 時每秒換下一張
const MAX_PREVIEW_WIDTH = 200;

// EXPAND_VIDEO_BANNER_1：videoPreview sprite，滑鼠 hover 從第一張開始每秒輪播下一張。
const VideoSpritePreview: React.FC<{ spriteUrl: string }> = ({ spriteUrl }) => {
  const [frame, setFrame] = useState(0);
  const [frameSize, setFrameSize] = useState<{ w: number; h: number } | undefined>();
  const timerRef = useRef<number | undefined>();

  // 讀 sprite 原始尺寸，推算單張畫格比例（總寬 / 5）以決定預覽容器長寬。
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setFrameSize({ w: img.naturalWidth / SPRITE_FRAMES, h: img.naturalHeight });
      }
    };
    img.src = spriteUrl;
  }, [spriteUrl]);

  useEffect(() => () => window.clearInterval(timerRef.current), []);

  const startCycling = () => {
    window.clearInterval(timerRef.current);
    setFrame(0);
    timerRef.current = window.setInterval(() => {
      setFrame((prev) => (prev + 1) % SPRITE_FRAMES);
    }, FRAME_INTERVAL_MS);
  };

  const stopCycling = () => {
    window.clearInterval(timerRef.current);
    setFrame(0);
  };

  if (!frameSize) {
    return <div className={styles.normalCreativePreview} />;
  }

  const width = Math.min(frameSize.w, MAX_PREVIEW_WIDTH);
  const height = width * (frameSize.h / frameSize.w);

  return (
    <div className={styles.normalCreativePreview}>
      <div
        className={styles.richMediaVideoPreview}
        onMouseEnter={startCycling}
        onMouseLeave={stopCycling}
        style={{
          width,
          height,
          backgroundImage: `url(${spriteUrl})`,
          backgroundSize: `${SPRITE_FRAMES * 100}% 100%`,
          // 水平 sprite：0% → 25% → 50% → 75% → 100% 對齊第 1~5 張
          backgroundPositionX: `${(frame / (SPRITE_FRAMES - 1)) * 100}%`
        }}
      />
    </div>
  );
};

export const RichMediaCreativePreview: React.FC<{ creative: CreativeOfCampaign | CreativeListRecord }> = (props) => {
  const creativeValue = parseCreativeValue(props.creative);
  const templateId = get(creativeValue, 'templateId');
  const bannerImageUrl = props.creative.bannerImageUrl;
  const videoPreview: string | undefined = get(props.creative, 'bannerExtra.videoPreview') as string;

  if (templateId === RichMediaTemplateId.EXPAND_VIDEO) {
    // 影片版位：有 sprite 就輪播，沒有就顯示 video placeholder。
    return videoPreview ? (
      <VideoSpritePreview spriteUrl={videoPreview} />
    ) : (
      <div className={styles.normalCreativePreview}>
        <img src={videoPlaceholder} alt={'preview'} />
      </div>
    );
  }

  // EXPAND_IMAGE_BANNER_1：直接顯示 bannerImageUrl。
  if (!bannerImageUrl) {
    return <div />;
  }
  return (
    <div className={styles.normalCreativePreview}>
      <img src={bannerImageUrl} alt={'preview'} />
    </div>
  );
};
